import { useState } from 'react'
import { useAlertStore } from '../stores/alertStore'
import { cn } from '../lib/utils'

const PRESET_COLORS = [
  '#fef08a', // Yellow
  '#fca5a5', // Red
  '#86efac', // Green
  '#93c5fd', // Blue
  '#c4b5fd', // Purple
  '#fdba74', // Orange
]

function KeywordManager() {
  const { keywords, addKeyword, deleteKeyword, toggleKeyword, testAlert } = useAlertStore()

  const [newKeyword, setNewKeyword] = useState('')
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [isRegex, setIsRegex] = useState(false)
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0])
  const [alertSound, setAlertSound] = useState(true)
  const [alertBrowser, setAlertBrowser] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newKeyword.trim()) return

    setIsAdding(true)
    setError(null)

    // Validate regex if needed
    if (isRegex) {
      try {
        new RegExp(newKeyword)
      } catch {
        setError('Invalid regex pattern')
        setIsAdding(false)
        return
      }
    }

    const result = await addKeyword({
      keyword: newKeyword.trim(),
      case_sensitive: caseSensitive,
      is_regex: isRegex,
      alert_sound: alertSound,
      alert_browser: alertBrowser,
      color: selectedColor,
    })

    if (!result.success) {
      setError(result.error || 'Failed to add keyword')
    } else {
      setNewKeyword('')
      setCaseSensitive(false)
      setIsRegex(false)
    }

    setIsAdding(false)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this keyword?')) return
    await deleteKeyword(id)
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        testAlert()
      }
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Keyword Alerts
        </h2>
        <button
          onClick={requestNotificationPermission}
          className="btn-secondary text-sm"
        >
          Test Alert
        </button>
      </div>

      {/* Add Keyword Form */}
      <form onSubmit={handleAdd} className="card p-4 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
          Add New Keyword
        </h3>

        {error && (
          <div className="p-3 mb-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Keyword or Pattern
            </label>
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="Enter keyword..."
              className="input"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Case sensitive
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isRegex}
                onChange={(e) => setIsRegex(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Regex pattern
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={alertSound}
                onChange={(e) => setAlertSound(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Sound alert
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={alertBrowser}
                onChange={(e) => setAlertBrowser(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Browser notification
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Highlight Color
            </label>
            <div className="flex gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={cn(
                    'w-8 h-8 rounded-full border-2 transition-all',
                    selectedColor === color
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={isAdding || !newKeyword.trim()}
            className={cn(
              'btn-primary',
              (isAdding || !newKeyword.trim()) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isAdding ? 'Adding...' : 'Add Keyword'}
          </button>
        </div>
      </form>

      {/* Keyword List */}
      <div className="card divide-y divide-gray-200 dark:divide-gray-700">
        {keywords.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
              />
            </svg>
            No keywords added yet. Add keywords to receive alerts!
          </div>
        ) : (
          keywords.map((keyword) => (
            <div
              key={keyword.id}
              className="p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                {/* Color indicator */}
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: keyword.color }}
                />

                {/* Keyword info */}
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className="font-medium text-gray-900 dark:text-white px-2 py-0.5 rounded"
                      style={{ backgroundColor: keyword.color }}
                    >
                      {keyword.keyword}
                    </span>
                    {keyword.is_regex && (
                      <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                        regex
                      </span>
                    )}
                    {keyword.case_sensitive && (
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                        case-sensitive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {keyword.alert_sound && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                        Sound
                      </span>
                    )}
                    {keyword.alert_browser && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        Browser
                      </span>
                    )}
                    {keyword.alert_email && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleKeyword(keyword.id)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                    keyword.enabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  )}
                >
                  {keyword.enabled ? 'Enabled' : 'Disabled'}
                </button>

                <button
                  onClick={() => handleDelete(keyword.id)}
                  className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Delete keyword"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default KeywordManager
